// The admin page's Slow Burn tab: headline numbers, how runs ended, where they ended, and every
// day in the window. Built from the daily analytics' card, table and styles.
import { useRef, useState } from 'react'
import type { AdminSlowBurn, AnalyticsDays } from '@wbwl/contract'
import { useAdminSlowBurn } from '../lib/data.ts'
import { dayDate, fmt } from '../lib/game.ts'
import { Card, ROWS_SHOWN, ShowAll, WindowTabs } from './analytics-ui.tsx'
import { ErrorNote } from './shell.tsx'
import { DepthHistogram } from './slow-burn-ui.tsx'

const pct = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : '—')
const num = (n: number | null) => (n === null ? '—' : fmt(n))

export function SlowBurnStats() {
  const [days, setDays] = useState<AnalyticsDays>(30)
  const res = useAdminSlowBurn(days)
  // Switching windows keeps the last one on screen, dimmed, until the new one arrives.
  const last = useRef<AdminSlowBurn | null>(null)
  if (res.data) last.current = res.data
  const a = res.data ?? last.current
  return (
    <section className="an">
      <div className="an-head">
        <h3>Slow Burn</h3>
        <WindowTabs days={days} set={setDays} />
        {a && <span className="faint small an-span">{a.days === 1 ? 'Today so far' : `${dayDate(a.since)} to today so far`}</span>}
      </div>
      {res.error && !res.data ? <ErrorNote error={res.error} retry={() => res.reload()} />
        : !a ? <p className="faint empty-note">Counting…</p>
        : (
          <div className={`an-body${res.data ? '' : ' stale'}`}>
            <Kpis a={a} />
            <div className="an-grid">
              <Card title="Where runs ended" sub="finished runs, by the row they reached">
                {a.kpis.finished ? <DepthHistogram bins={a.depths} you={null} height={110} /> : <p className="muted small">No finished runs in this window.</p>}
              </Card>
              <Card title="How runs ended" sub="every run with a word played">
                <Ends a={a} />
              </Card>
            </div>
            <Card title="Days" sub="newest first">
              <Days a={a} />
            </Card>
          </div>
        )}
    </section>
  )
}

function Kpis({ a }: { a: AdminSlowBurn }) {
  const k = a.kpis
  const tiles = [
    { label: 'Players', value: fmt(k.players), note: 'with a run in the window' },
    a.days === 1
      ? { label: 'Runs', value: fmt(k.started), note: 'today' }
      : { label: 'Runs a day', value: (k.started / a.days).toFixed(1), note: `${fmt(k.started)} runs, average` },
    { label: 'Finish rate', value: pct(k.finished, k.started), note: `${fmt(k.finished)} of ${fmt(k.started)} ended` },
    { label: 'Also played the daily', value: pct(k.bothGames, k.players), note: `${fmt(k.bothGames)} of ${fmt(k.players)} players, the same day` },
    { label: 'Median points', value: num(k.medianPoints), note: 'finished runs' },
    { label: 'Median row', value: num(k.medianDepth), note: 'reached, finished runs' },
  ]
  return (
    <div className="an-kpis sb-kpis">
      {tiles.map((t) => (
        <div className="kpi" key={t.label}>
          <span className="kl">{t.label}</span>
          <b>{t.value}</b>
          <span className="ft"><span className="nt">{t.note}</span></span>
        </div>
      ))}
    </div>
  )
}

function Ends({ a }: { a: AdminSlowBurn }) {
  const e = a.ends
  const total = e.burnedOut + e.endedEarly + e.fuseOut + e.dayOver + e.unfinished
  if (!total) return <p className="muted small">No runs in this window.</p>
  const rows = [
    { label: 'Burned out', note: 'no word fit', n: e.burnedOut },
    { label: 'Ended early', note: 'the player stopped', n: e.endedEarly },
    { label: 'Fuse ran out', note: 'endgame clock', n: e.fuseOut },
    { label: 'Day ended', note: 'still going at midnight', n: e.dayOver },
    { label: 'Unfinished', note: 'today’s, still going', n: e.unfinished },
  ]
  return (
    <div className="sb-ends">
      {rows.map((r) => (
        <div key={r.label} className="sb-end">
          <span className="l"><b>{r.label}</b><span className="faint small">{r.note}</span></span>
          <span className="bar"><i style={{ width: `${(r.n / total) * 100}%` }} /></span>
          <span className="n">{fmt(r.n)} <small>{pct(r.n, total)}</small></span>
        </div>
      ))}
    </div>
  )
}

function Days({ a }: { a: AdminSlowBurn }) {
  const [all, setAll] = useState(false)
  if (!a.perDay.length) return <p className="muted small">No Slow Burn days in this window.</p>
  return (
    <div className="an-table-wrap">
      <table className="an-table">
        <thead>
          <tr>
            <th>No.</th><th className="l">Day</th><th>Runs</th><th>Finished</th><th>Best<br />points</th><th>Solver’s<br />best</th>
            <th>Median<br />points</th><th>Median<br />row</th><th>Deepest<br />row</th><th>Burned<br />out</th><th>Ended<br />early</th><th>Fuse<br />ran out</th><th>Day<br />ended</th><th>Median<br />time</th>
          </tr>
        </thead>
        <tbody>
          {(all ? a.perDay : a.perDay.slice(0, ROWS_SHOWN)).map((d) => (
            <tr key={d.number}>
              <td><b>{d.number}</b></td>
              <td className="l">{dayDate(d.date)}</td>
              <td>{fmt(d.started)}</td>
              <td>{fmt(d.finished)} <small>{pct(d.finished, d.started)}</small></td>
              <td>{num(d.bestPoints)}</td>
              <td>{fmt(d.solverPoints)} <small>row {d.solverDepth}</small></td>
              <td>{num(d.medianPoints)}</td>
              <td>{num(d.medianDepth)}</td>
              <td>{num(d.deepest)}</td>
              <td>{fmt(d.burnedOut)}</td>
              <td>{fmt(d.endedEarly)}</td>
              <td>{fmt(d.fuseOut)}</td>
              <td>{fmt(d.dayOver)}</td>
              <td>{d.medianMinutes === null ? '—' : `${d.medianMinutes < 10 ? d.medianMinutes.toFixed(1) : Math.round(d.medianMinutes)}m`}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <ShowAll all={all} set={setAll} total={a.perDay.length} noun="days" />
    </div>
  )
}
